import React, { useState } from "react";
import { Modal, Button } from "flowbite-react";
import { 
  ExclamationTriangleIcon, 
  XCircleIcon, 
  UserMinusIcon,
  LockClosedIcon,
  EyeIcon,
  EyeSlashIcon
} from "@heroicons/react/24/outline";
import { useAuth } from "../context/AuthContext";

const DeleteAccountModal = ({ isOpen, onClose, onDeleteAccount }) => {
  const { deleteAccount } = useAuth();
  const [confirmText, setConfirmText] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPasswordField, setShowPasswordField] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const expectedConfirmation = "delete my account";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!showPasswordField && confirmText.toLowerCase() !== expectedConfirmation) {
      setError(`Please type "${expectedConfirmation}" to confirm deletion`);
      return;
    }

    if (showPasswordField && !password) {
      setError("Please enter your password to confirm deletion");
      return;
    }

    if (!showPasswordField) {
      setShowPasswordField(true);
      return;
    }

    setIsSubmitting(true);
    
    try {
      console.log("Attempting to delete account");
      const response = await deleteAccount(password);
      
      if (response && response.success) {
        console.log("Account deleted successfully");
        onDeleteAccount();
        resetForm();
        onClose();
      } else {
        console.error("Account deletion failed:", response);
        setError(response?.message || "Failed to delete account. Please try again later.");
        setIsSubmitting(false);
      }
    } catch (err) {
      console.error("Account deletion error:", err);
      
      if (!err?.response) {
        setError("No response from server. Please try again later.");
      } else if (err.response?.status === 403) {
        setError("You don't have permission to delete this account.");
      } else if (err.response?.status === 401) {
        setError("Incorrect password. Please check your password and try again.");
      } else {
        setError("Failed to delete account. Please try again later.");
      }
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setConfirmText("");
    setPassword("");
    setError("");
    setShowPasswordField(false);
  };

  const closeModal = () => {
    resetForm();
    onClose();
  };

  return (
    <Modal 
      show={isOpen} 
      onClose={closeModal} 
      size="md"
      className="rounded-xl overflow-hidden shadow-2xl"
    >
      <Modal.Header className="border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-red-50 to-orange-50 dark:from-gray-800 dark:to-gray-900 text-gray-800 dark:text-white">
        <div className="flex items-center gap-3">
          <UserMinusIcon className="h-6 w-6 text-red-600 dark:text-red-500" />
          <h3 className="text-xl font-semibold">Delete Account</h3>
        </div>
      </Modal.Header>
      <Modal.Body className="px-6 py-6 bg-white dark:bg-gray-900">
        <div className="flex flex-col items-center text-center mb-6">
          <div className="bg-red-100 dark:bg-red-900/30 p-3 rounded-full mb-4 animate-pulse">
            <ExclamationTriangleIcon className="h-12 w-12 text-red-600 dark:text-red-500" />
          </div>
          <h3 className="text-xl font-medium text-gray-900 dark:text-white mb-3">
            Are you sure you want to delete your account?
          </h3>
          <p className="text-gray-600 dark:text-gray-400 max-w-md">
            This action is <span className="font-semibold text-red-600 dark:text-red-500">permanent</span> and cannot be undone. All your data, including your profile, tokens, and transaction history will be permanently deleted.
          </p>
        </div>

        {error && (
          <div className="flex p-4 mb-5 text-red-800 bg-red-50 dark:bg-red-900/20 dark:text-red-400 border-l-4 border-red-500 rounded-lg shadow-sm">
            <XCircleIcon className="h-6 w-6 mr-3 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {!showPasswordField ? (
        <div className="bg-gray-50 dark:bg-gray-800 p-5 rounded-lg border border-gray-200 dark:border-gray-700 mb-5 shadow-sm">
          <div className="flex items-start mb-4">
            <LockClosedIcon className="h-6 w-6 mr-3 flex-shrink-0 text-amber-500 dark:text-amber-400" />
            <p className="text-sm text-gray-700 dark:text-gray-300">
              To confirm account deletion, please type <span className="font-medium text-red-600 dark:text-red-500 bg-red-100 dark:bg-red-900/30 px-1.5 py-0.5 rounded">delete my account</span> in the field below:
            </p>
          </div>
          <div className="relative">
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              className={`w-full px-4 py-3 rounded-lg border ${
                confirmText && confirmText.toLowerCase() !== expectedConfirmation && confirmText.length >= expectedConfirmation.length
                ? "border-red-500 dark:border-red-500" 
                : confirmText.toLowerCase() === expectedConfirmation
                ? "border-green-500 dark:border-green-500" 
                : "border-gray-300 dark:border-gray-700"
              } bg-white dark:bg-gray-800 text-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500 transition-all duration-300`}
              placeholder="Type 'delete my account'"
              autoComplete="off"
            />
            {confirmText.toLowerCase() === expectedConfirmation && (
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-green-500">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
            )}
          </div>
        </div>
        ) : (
          <div className="bg-gray-50 dark:bg-gray-800 p-5 rounded-lg border border-gray-200 dark:border-gray-700 mb-5 shadow-sm animate-fadeIn">
            <div className="flex items-start mb-4">
              <LockClosedIcon className="h-6 w-6 mr-3 flex-shrink-0 text-amber-500 dark:text-amber-400" />
              <p className="text-sm text-gray-700 dark:text-gray-300">
                For security, please enter your password to confirm account deletion:
              </p>
            </div>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500 transition-all duration-300"
                placeholder="Enter your password"
                autoComplete="current-password"
                autoFocus
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? 
                  <EyeIcon className="h-5 w-5" /> : 
                  <EyeSlashIcon className="h-5 w-5" />
                }
              </button>
            </div>
          </div>
        )}

        {isSubmitting && (
          <div className="flex justify-center items-center gap-2 text-sm text-gray-600 dark:text-gray-400 animate-pulse">
            <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
            <span>Processing your request...</span>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
        <Button 
          color="failure" 
          onClick={handleSubmit} 
          disabled={isSubmitting || (!showPasswordField && confirmText.toLowerCase() !== expectedConfirmation) || (showPasswordField && !password)}
          className={`bg-red-600 hover:bg-red-700 transition-all duration-300 ${
            isSubmitting || (!showPasswordField && confirmText.toLowerCase() !== expectedConfirmation) || (showPasswordField && !password)
              ? "opacity-60 cursor-not-allowed" 
              : "hover:shadow-lg"
          }`}
        >
          {isSubmitting ? (
            <div className="flex items-center gap-2">
              <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
              <span>Deleting...</span>
            </div>
          ) : showPasswordField ? (
            <span>Confirm Deletion</span>
          ) : (
            <span>Continue</span>
          )}
        </Button>
        <Button 
          color="gray" 
          onClick={closeModal}
          className="hover:bg-gray-200 dark:hover:bg-gray-700 transition-all duration-300"
        >
          Cancel
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DeleteAccountModal; 